import { Component, computed, inject, OnInit, signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { RecoveryFlow, SettingsFlow, UiText } from '@ory/client';
import { VxButtonComponent } from '@ui/button/button.component';
import { VxFormFieldComponent } from '@ui/form-field/form-field.component';
import { VxInputComponent } from '@ui/input/input.component';
import {
  csrfToken,
  flowMessages,
  flowValue,
  submitFlow,
} from 'src/app/services/authentication/kratos-flow';
import {
  ErrAlreadyLoggedIn,
  ErrBrowserLocationChangeRequired,
  ErrReauthenticationNeeded,
  ErrTwoFARequired,
} from 'src/app/services/authentication/kratos.errors';
import { KratosAuthenticationService } from 'src/app/services/authentication/kratos.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import { TranslationService } from 'src/app/services/translation/translation.service';
import { AuthHeaderComponent } from '../components/auth-header/auth-header.component';
import { AuthHelpComponent } from '../components/auth-help/auth-help.component';
import { AuthPanelComponent } from '../components/auth-panel/auth-panel.component';
import { AuthShellComponent } from '../components/auth-shell/auth-shell.component';
import { CodeEntryComponent } from 'src/app/core/components/code-entry/code-entry.component';
import { codeStep } from 'src/app/core/components/code-entry/code-step';
import { CodeResendComponent } from '../components/code-resend/code-resend.component';
import { resendCooldown } from '../components/code-resend/resend-cooldown';
import { KratosMessagesComponent } from 'src/app/core/components/kratos-messages/kratos-messages.component';
import { RegisterPageState } from '../register/register.component';

type RecoveryStage = 'email' | 'code' | 'password';

/** Why the email step starts over: the code ran out, or Kratos is unreachable. */
type RecoveryNotice = 'expired' | 'failed';

/** What a submitted code did: signed the person in, or the flow is gone. */
type CodeOutcome = 'recovered' | 'expired' | RecoveryFlow;

/**
 * "Forgot password?": the address gets a 6-digit code (Kratos recovery,
 * `use: code`), the code signs the person in, then they choose a new
 * password (a settings flow). Someone already signed in starts at the
 * password step.
 */
@Component({
  selector: 'app-recovery-page',
  templateUrl: './recovery.component.html',
  imports: [
    ReactiveFormsModule,
    RouterModule,
    TranslocoModule,
    AuthHeaderComponent,
    AuthHelpComponent,
    AuthPanelComponent,
    AuthShellComponent,
    CodeEntryComponent,
    CodeResendComponent,
    KratosMessagesComponent,
    VxButtonComponent,
    VxFormFieldComponent,
    VxInputComponent,
  ],
})
export class RecoveryPageComponent implements OnInit {
  private readonly auth = inject(KratosAuthenticationService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly translation = inject(TranslationService);

  protected readonly stage = signal<RecoveryStage>('email');

  private readonly recoveryFlow = signal<RecoveryFlow | undefined>(undefined);

  private readonly settingsFlow = signal<SettingsFlow | undefined>(undefined);

  /** "Forgot password?" on the login page hands the address over. */
  protected readonly emailForm = new FormGroup({
    email: new FormControl(
      (this.router.getCurrentNavigation()?.extras.state as RegisterPageState)
        ?.email ?? '',
      { nonNullable: true, validators: [Validators.required, Validators.email] }
    ),
  });

  protected readonly passwordForm = new FormGroup({
    password: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  /** The code boxes; their busy state covers the page's other steps too. */
  protected readonly step = codeStep();

  protected readonly cooldown = resendCooldown();

  /** Kratos's errors for the current step. */
  protected readonly messages = signal<UiText[]>([]);

  protected readonly notice = signal<RecoveryNotice | null>(null);

  /** The address the code went to. */
  private readonly sentTo = signal('');

  /** The address the code went to, or the signed-in account's. */
  protected readonly email = computed(() => {
    const settings = this.settingsFlow();
    return settings ? flowValue(settings, 'traits.email') : this.sentTo();
  });

  /**
   * The settings flow is there to take the new password, or it failed to
   * load and saving tries again.
   */
  protected readonly passwordReady = computed(
    () => !!this.settingsFlow() || this.notice() === 'failed'
  );

  ngOnInit(): void {
    this.openFlow();
  }

  protected async sendCode(): Promise<void> {
    const { email } = this.emailForm.controls;
    if (email.invalid) {
      email.markAsTouched();
      return;
    }
    const address = email.value.trim();
    if (await this.requestCode(address)) {
      this.sentTo.set(address);
      this.stage.set('code');
    }
  }

  /** Sending the address again invalidates the old code and mails a new one. */
  protected resend(): void {
    if (this.cooldown.remaining() === 0) {
      this.requestCode(this.sentTo());
    }
  }

  protected async submitCode(code: string): Promise<void> {
    const flow = this.recoveryFlow();
    if (!flow) {
      return;
    }
    const outcome = await this.step.verify(() => this.checkCode(flow, code));
    if (outcome === 'recovered') {
      this.startPasswordStage();
    } else if (outcome === 'expired') {
      this.restart('expired');
    } else if (outcome) {
      this.useRecoveryFlow(outcome);
      this.step.reject();
    }
  }

  /** Back to the address, with a fresh flow (the old code stops working). */
  protected changeEmail(): void {
    this.emailForm.controls.email.setValue(this.sentTo());
    this.openFlow();
  }

  protected savePassword(): void {
    const { password } = this.passwordForm.controls;
    if (password.invalid) {
      password.markAsTouched();
      return;
    }
    this.notice.set(null);
    this.step.run(async () => {
      const flow = this.settingsFlow() ?? (await this.loadSettingsFlow());
      if (!flow) {
        return;
      }
      const result = await submitFlow<SettingsFlow, void>(() =>
        this.auth.completeSettingsFlow(flow.id, 'password', {
          method: 'password',
          password: password.value,
          csrf_token: csrfToken(flow),
        })
      );
      if (result.ok) {
        this.toast.showInfo(
          'pages.auth.recovery.updated.header',
          'pages.auth.recovery.updated.body'
        );
        this.router.navigate(['/']);
      } else if (result.error instanceof ErrReauthenticationNeeded) {
        // the code's 15 minutes ran out; settings confirms it's them again
        this.toast.showInfo(
          'pages.auth.recovery.continueInSettings.header',
          'pages.auth.recovery.continueInSettings.body'
        );
        this.router.navigate(['/settings/account/security']);
      } else if (result.flow) {
        this.settingsFlow.set(result.flow);
        this.messages.set(this.errors(result.flow));
      } else {
        this.notice.set('failed');
      }
    });
  }

  /**
   * Mails a code to `address`, opening a flow first if the page couldn't
   * yet. Resolves whether Kratos sent it.
   */
  private async requestCode(address: string): Promise<boolean> {
    const sent = await this.step.run(async () => {
      this.notice.set(null);
      const flow = this.recoveryFlow() ?? (await this.openFlow());
      if (!flow) {
        return false;
      }
      const result = await submitFlow<RecoveryFlow, RecoveryFlow>(() =>
        this.auth.completeRecoveryFlow(flow, {
          method: 'code',
          email: address,
          csrf_token: csrfToken(flow),
          transient_payload: {
            flow_id: flow.id,
            locale: this.translation.currentLocale,
          },
        })
      );
      // Kratos answers both 200 and 400 with the flow
      const next = result.ok ? result.value : result.flow;
      if (!next) {
        this.restart('expired');
        return false;
      }
      this.useRecoveryFlow(next);
      return next.state === 'sent_email';
    });
    if (sent) {
      this.step.reset();
      this.cooldown.start();
    }
    return !!sent;
  }

  /** What a code did: signed the person in, didn't, or the flow is gone. */
  private async checkCode(
    flow: RecoveryFlow,
    code: string
  ): Promise<CodeOutcome> {
    const result = await submitFlow<RecoveryFlow, RecoveryFlow>(() =>
      this.auth.completeRecoveryFlow(flow, {
        method: 'code',
        code,
        csrf_token: csrfToken(flow),
      })
    );
    if (result.ok) {
      return this.recovered(result.value) ? 'recovered' : result.value;
    }
    // the code was right: Kratos signed the person in
    if (result.error instanceof ErrBrowserLocationChangeRequired) {
      return 'recovered';
    }
    return result.flow ?? 'expired';
  }

  /**
   * Newer Kratos reports a correct code in the flow itself instead of the
   * browser redirect.
   */
  private recovered(flow: RecoveryFlow): boolean {
    return (
      flow.state === 'passed_challenge' ||
      !!flow.continue_with?.some(item => item.action === 'show_settings_ui')
    );
  }

  /** A fresh flow on the email step; signed in already skips to the password. */
  private async openFlow(): Promise<RecoveryFlow | undefined> {
    try {
      const flow = await this.auth.getRecoveryFlow();
      this.recoveryFlow.set(flow);
      this.messages.set([]);
      this.stage.set('email');
      return flow;
    } catch (error) {
      if (error instanceof ErrAlreadyLoggedIn) {
        this.startPasswordStage();
      } else {
        this.notice.set('failed');
      }
      return undefined;
    }
  }

  /** Back to the email step, with the address kept and why it starts over. */
  private restart(notice: RecoveryNotice): void {
    const { email } = this.emailForm.controls;
    email.setValue(this.sentTo() || email.value);
    this.openFlow().then(flow => {
      // a flow that can't be opened keeps its own "failed" notice
      if (flow) {
        this.notice.set(notice);
      }
    });
  }

  private useRecoveryFlow(flow: RecoveryFlow): void {
    this.recoveryFlow.set(flow);
    // "a code was sent" is what the code step already says
    this.messages.set(this.errors(flow));
  }

  private errors(flow: RecoveryFlow | SettingsFlow): UiText[] {
    return flowMessages(flow).filter(message => message.type === 'error');
  }

  private async startPasswordStage(): Promise<void> {
    this.messages.set([]);
    this.stage.set('password');
    await this.loadSettingsFlow();
  }

  /** The flow that takes the new password; "failed" when it can't load. */
  private async loadSettingsFlow(): Promise<SettingsFlow | undefined> {
    try {
      const flow = await this.auth.getSettingsFlow();
      this.settingsFlow.set(flow);
      return flow;
    } catch (error) {
      if (error instanceof ErrTwoFARequired) {
        this.router.navigate(['/login'], {
          queryParams: { aal: 'aal2', returnTo: '/recovery' },
        });
      } else {
        this.notice.set('failed');
      }
      return undefined;
    }
  }
}
